import React from 'react';
import { first, pickBy, identity, isEmpty, unescape } from 'lodash-es';
import { type RealEstateAgent, type WithContext } from 'schema-dts';
import { SKIP_RENDER, UNDEFINED } from '@is24at/react-constants';
import { TestId } from 'utils/constants';
import { REALTOR_PROFILE_PRO_FEATURE } from 'utils/enums';

const htmlToPlainText = (html = '') =>
  unescape(html.replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

type Props = {
  companyName?: string;
  phoneNumbers?: string[];
  logoUrl?: string;
  website?: string;
  proRealtorAboutUsText?: string;
  features?: string[];
  streetString?: string;
  city?: string;
  countryCode?: string;
  zipCode?: string;
  exposePath?: string;
  baseUrl?: string;
};

export const RealEstateAgentStructuredData: React.FC<Props> = ({
  ...props
}) => {
  const {
    companyName,
    phoneNumbers,
    logoUrl,
    website,
    proRealtorAboutUsText,
    features,
    streetString,
    city,
    countryCode,
    zipCode,
    exposePath,
    baseUrl,
  } = props;

  if (isEmpty(props)) {
    return SKIP_RENDER;
  }

  const maybeAddress = pickBy(
    {
      streetAddress: streetString,
      addressLocality: city,
      postalCode: zipCode,
      addressCountry: countryCode,
    },
    identity,
  );

  const address = isEmpty(maybeAddress)
    ? UNDEFINED
    : {
        '@type': 'PostalAddress',
        ...maybeAddress,
      };
  const hasVisibleDescription = features?.includes(REALTOR_PROFILE_PRO_FEATURE);
  const description = hasVisibleDescription
    ? htmlToPlainText(proRealtorAboutUsText)
    : UNDEFINED;

  const realEstateAgentStructuredData: WithContext<RealEstateAgent> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': `${baseUrl || ''}${exposePath || ''}`,
    name: companyName,
    description,
    image: logoUrl,
    url: website,
    telephone: first(phoneNumbers),
    address,
  };

  return (
    <script
      type="application/ld+json"
      data-testid={TestId.REAL_ESTATE_AGENT_STRUCTURED_DATA}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(pickBy(realEstateAgentStructuredData, identity)),
      }}
    />
  );
};
